import { createOAuthStateNonce } from '../auth/oauthState';

/**
 * Short-lived storage for pending federated (SSO) login attempts.
 *
 * Before redirecting to Cognito we persist a `nonce -> tenantCode` binding, and on the way back
 * we look the nonce up again to confirm the callback belongs to a login this browser started.
 *
 * This deliberately uses localStorage rather than sessionStorage: sessionStorage is exactly what
 * Safari drops across the Cognito round-trip (HRI2-16956), so keeping the CSRF nonce there would
 * guarantee the check fails on the very browser this fix targets. localStorage is same-origin and
 * survives the redirect chain.
 *
 * Only the OAuth nonce record lives here. Tenant *selection* stays in sessionStorage
 * (`tenantSessionStorage`) so that two tabs on two tenants cannot cross-contaminate.
 */

const pendingSsoStatesKey = 'ot_pendingSsoStates';

/**
 * How long a pending login attempt stays valid.
 *
 * Real SSO flows routinely include IdP MFA prompts, consent screens and password resets, so this
 * is generous: expiry degrades to `'absent'`, which soft-fails and proceeds, and a short TTL
 * would therefore silently drop CSRF protection for slow-but-legitimate logins.
 */
export const pendingSsoStateTtlMs = 30 * 60 * 1000;

/** Cap on concurrent in-flight logins (e.g. several tabs). Oldest entries are evicted first. */
export const maxPendingSsoStates = 5;

/** Persisted shape. Kept short because several records share one storage entry. */
type PendingSsoStateRecord = {
  n: string;
  t: string;
  ts: number;
};

export type ConsumePendingSsoStateResult = 'matched' | 'mismatch' | 'absent' | 'unavailable';

/** Prefix for the sentinel that tells "storage is empty" apart from "storage does not work". */
const storageProbeKeyPrefix = `${pendingSsoStatesKey}_probe`;

let probeSequence = 0;

/**
 * Builds a sentinel key that is unique per call.
 *
 * A fixed key would let two contexts probing at the same time overwrite and delete each other's
 * sentinel, so a healthy browser could read back the wrong value and be reported as unusable -
 * and `'unavailable'` is the one result that skips verification. This only needs uniqueness, not
 * unpredictability, so it deliberately avoids `crypto.getRandomValues`: that can throw, and a
 * throw here would land in the `catch` below and fail open in exactly the same way.
 */
function nextStorageProbeKey(): string {
  probeSequence += 1;
  return `${storageProbeKeyPrefix}_${Date.now()}_${probeSequence}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Mints a nonce for a login attempt, records it against the tenant and returns it so the caller
 * can put it in the OAuth `state` parameter.
 */
export function createPendingSsoState(tenantId: string): string {
  const nonce = createOAuthStateNonce();
  const records = readPendingSsoStates();

  writePendingSsoStates([...records, { n: nonce, t: tenantId, ts: Date.now() }].slice(-maxPendingSsoStates));

  return nonce;
}

/**
 * Looks up a nonce and removes it - records are single use, so a replayed callback never
 * reports `'matched'` twice.
 *
 * - `'matched'`     - the nonce was found and is bound to `tenantId`.
 * - `'mismatch'`    - the nonce is bound to another tenant, or is unknown while other pending
 *                     attempts exist (so this is not the login we started: stale or forged).
 * - `'absent'`      - storage works and holds no matching attempt, so this browser never
 *                     started this sign-in. Callers must reject: this is the login-CSRF case.
 * - `'unavailable'` - storage cannot be written at all, so no record could ever have been
 *                     kept and its absence proves nothing.
 *
 * Distinguishing the last two matters: treating an empty-but-working store as `'unavailable'`
 * would make the whole check bypassable simply by clearing site data.
 */
export function consumePendingSsoState(nonce: string, tenantId: string): ConsumePendingSsoStateResult {
  const records = readPendingSsoStates();
  const record = records.find((r) => r.n === nonce);

  if (!record) {
    if (records.length > 0) {
      return 'mismatch';
    }
    return isStorageUsable() ? 'absent' : 'unavailable';
  }

  writePendingSsoStates(records.filter((r) => r.n !== nonce));

  return record.t === tenantId ? 'matched' : 'mismatch';
}

/**
 * Checks that localStorage can actually be written and read back.
 *
 * Both halves matter: some browsers throw on write (Safari private mode historically did),
 * others accept the write and silently discard it under a zero quota, so the value is read back
 * before concluding that storage works.
 */
function isStorageUsable(): boolean {
  const probeKey = nextStorageProbeKey();
  try {
    const probeValue = `${Date.now()}`;
    localStorage.setItem(probeKey, probeValue);
    return localStorage.getItem(probeKey) === probeValue;
  } catch {
    return false;
  } finally {
    // Always clean up, including when the read above threw. Keys are unique per call, so a leak
    // here would accumulate rather than being overwritten next time.
    try {
      localStorage.removeItem(probeKey);
    } catch {
      // Storage is unusable anyway - there is nothing to clean up.
    }
  }
}

/**
 * Reads the pending records, dropping expired ones. Every access is guarded: Safari private mode
 * can throw on localStorage, and an unreadable store simply means "no pending attempts".
 */
function readPendingSsoStates(): PendingSsoStateRecord[] {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(pendingSsoStatesKey);
  } catch {
    return [];
  }

  if (!stored) {
    return [];
  }

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  const now = Date.now();
  const validRecords = parsed.filter(
    (record): record is PendingSsoStateRecord =>
      typeof record === 'object' &&
      record !== null &&
      typeof record.n === 'string' &&
      typeof record.t === 'string' &&
      typeof record.ts === 'number' &&
      now - record.ts < pendingSsoStateTtlMs,
  );

  // Enforce the cap on read as well as on write, so a store that grew past it (another tab, an
  // older build, a hand-edited value) is brought back within bounds rather than kept forever.
  const records = validRecords.slice(-maxPendingSsoStates);

  if (records.length !== parsed.length) {
    writePendingSsoStates(records);
  }

  return records;
}

function writePendingSsoStates(records: PendingSsoStateRecord[]): void {
  try {
    if (records.length === 0) {
      localStorage.removeItem(pendingSsoStatesKey);
      return;
    }
    localStorage.setItem(pendingSsoStatesKey, JSON.stringify(records));
  } catch {
    // Storage is unavailable (e.g. Safari private mode). Verification then reports
    // 'unavailable' rather than 'absent' - see submitAuthCode in useAuthStore.
  }
}
